/* eslint-disable jsx-a11y/no-autofocus */
// TODO try to re-enable no-autofocus rule after closer examination
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';

import {
  ChakraBox,
  ChakraTabPanel,
  ChakraTabPanels,
  ChakraTabs,
  EquipUIFireflyV2Theme,
  FAIconName,
  Icon,
  IconSize,
  TextInput,
} from '@equip.health/ui';
import { debounce } from 'lodash';
import { useHistory } from 'react-router-dom';

import {
  SIDE_NAV_SMALL_WIDTH_GLOBALSEARCH,
  SIDE_NAV_WIDTH,
} from '~/lib/constants';
import urlConstants from '~/lib/constants/url.constants';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { GlobalSearchSuggestion } from '~/lib/types/navbar/global-search';

import { GlobalSearchTabs, GlobalSearchTabsCollection } from './constants';
import SearchResultsMaud from './SearchResultsMaud';
import TabList from './TabList';
import { SearchData, useSearchData } from './useSearchData';
import {
  addPhoneNumberPrefix,
  toInquirySuggestion,
  toPatientSuggestion,
} from './utils';
import {
  NAV_BOTTOM_PADDING,
  NAV_HORIZONTAL_PADDING,
  NAV_LOGO_HEIGHT,
  NAV_SEARCHBAR_PADDING_TOP,
  NAV_TOP_PADDING,
} from '../constants';
import NavTooltip from '../NavToolTip';

const {
  neutral: {
    line: neutralLine,
    primary: neutralPrimary,
    background: { primary: neutralBackgroundPrimary },
    tertiary: neutralTertiary,
    secondary: { 5: secondaryFive },
  },
} = EquipUIFireflyV2Theme.colors;

const TOP_LEVEL_ID = 'global-search';
const CLOSE_SEARCH_TIMEOUT_MS = 30 * 1000;

const getListItemId = (value: string): string => {
  return `${TOP_LEVEL_ID}__suggestions__list-item__${value}`;
};

const POPUP_PADDING = 16;
const TAB_LIST_HEIGHT = 42;
const TAB_PANEL_PADDING = 8;
const TEXT_INPUT_HEIGHT = 40;

const ABSOLUTE_MAX_LIST_HEIGHT = 700;

interface GlobalSearchProps {
  onSearchPanelVisibilityChange?: (isSearchPanelOver: boolean) => void;
  isSmall?: boolean;
}

const MagnifyingGlassIcon = (
  <Icon
    color={neutralPrimary}
    name={FAIconName.magnifyingGlassRegular}
    size={IconSize.medium}
  />
);
const GlobalSearchMaud = ({
  isSmall = false,
  onSearchPanelVisibilityChange,
}: GlobalSearchProps) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [isListVisible, setIsListVisible] = useState<boolean>(false);
  const [searchInputWidth, setSearchInputWidth] = useState(
    isSmall ? SIDE_NAV_SMALL_WIDTH_GLOBALSEARCH : SIDE_NAV_WIDTH,
  );
  const [currentTab, setCurrentTab] = useState<GlobalSearchTabs>(
    GlobalSearchTabs.PATIENTS,
  );
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<number>(0);
  const [isDebouncedOptionsLoading, setIsDebouncedOptionsLoading] =
    useState<boolean>(false);

  const [isSmallVariant, setIsSmallVariant] = useState(isSmall);

  const [closeSearchTimeoutId, setCloseSearchTimeoutId] = useState<number>();

  const [isFocused, setFocused] = useState<boolean>(false);
  const history = useHistory();

  const patients = useSearchData<Patient>({
    fetchUrl: urlConstants.users.getPatients,
    filterKey: 'patient-search',
    toAutoCompleteOptionMapper: toPatientSuggestion,
  });

  const contacts = useSearchData<Patient>({
    fetchUrl: urlConstants.users.getPatients,
    filterKey: 'contact-search',
    toAutoCompleteOptionMapper: toPatientSuggestion,
  });

  const inquiries = useSearchData<InquiryListItemResponse>({
    fetchUrl: urlConstants.inquiry.getInquiries,
    filterKey: 'inquiry-search',
    toAutoCompleteOptionMapper: toInquirySuggestion,
  });

  const dataObjectsByTab: Record<GlobalSearchTabs, SearchData> = {
    [GlobalSearchTabs.PATIENTS]: patients,
    [GlobalSearchTabs.PATIENTS_BY_CONTACT]: contacts,
    [GlobalSearchTabs.INQUIRIES]: inquiries,
  };

  const debouncedFetchOptions = useRef(
    debounce((searchTerm: string) => {
      patients.handleSearch(searchTerm);
      contacts.handleSearch(searchTerm);
      inquiries.handleSearch(addPhoneNumberPrefix(searchTerm));
    }, 500),
  ).current;

  useEffect(
    () => () => debouncedFetchOptions.cancel(),
    [debouncedFetchOptions],
  );

  const onChangeDebounced = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    e.stopPropagation();
    const input = e.target.value;
    const trimmedInput = input.trim();
    const inputNormalizedToEmpty = trimmedInput ? input : trimmedInput;

    setIsListVisible(!!inputNormalizedToEmpty);
    setInputValue(inputNormalizedToEmpty);
    onSearchPanelVisibilityChange(!!inputNormalizedToEmpty);
    if (!inputNormalizedToEmpty) {
      setCurrentTab(GlobalSearchTabs.PATIENTS);
    } else {
      // Prevent searching with a string that is just whitespace
      debouncedFetchOptions(trimmedInput);
      setIsDebouncedOptionsLoading(true);
    }
  };

  useEffect(() => {
    const isLoading =
      patients.isLoading || contacts.isLoading || inquiries.isLoading;
    setIsDebouncedOptionsLoading(isLoading);
  }, [patients.isLoading, contacts.isLoading, inquiries.isLoading]);

  const listIsOpen = isListVisible && inputValue;

  useEffect(() => {
    setActiveSuggestionIndex(0);
  }, [currentTab]);

  // Calculate the width of the input box based on variant flag
  const calculateTextInputWidth = () => {
    setSearchInputWidth(
      isSmall ? SIDE_NAV_SMALL_WIDTH_GLOBALSEARCH : SIDE_NAV_WIDTH,
    );

    setIsSmallVariant(isSmall);
  };

  // Check for small in variant type
  if (isSmall !== isSmallVariant) {
    calculateTextInputWidth();
  }

  const resetSearch = () => {
    setInputValue('');
    setActiveSuggestionIndex(0);
    setIsListVisible(false);
    onSearchPanelVisibilityChange(false);
    setCurrentTab(GlobalSearchTabs.PATIENTS);
  };

  const { setComponentVisibility } = useComponentPortalContext();

  const handleCloseInquiryDetailDrawer = () => {
    setComponentVisibility({
      component: PortalComponent.INQUIRY_DETAIL_DRAWER,
      data: null,
      isVisible: false,
    });
  };

  const handleOpenInquiryDetailDrawer = (inquiryId: string) => {
    setComponentVisibility({
      component: PortalComponent.INQUIRY_DETAIL_DRAWER,
      data: { inquiryId, onClose: handleCloseInquiryDetailDrawer },
      isVisible: true,
    });
  };

  const handleSelect = (suggestion: GlobalSearchSuggestion) => {
    if (currentTab === GlobalSearchTabs.INQUIRIES) {
      handleOpenInquiryDetailDrawer(suggestion.value);
      // Do not close or reset search upon opening an inquiry drawer unless user has not
      // interacted with the search for 30 seconds
      window.clearTimeout(closeSearchTimeoutId);
      setCloseSearchTimeoutId(
        window.setTimeout(resetSearch, CLOSE_SEARCH_TIMEOUT_MS),
      );
    } else {
      resetSearch();
      if (suggestion) {
        const { basePath } = GlobalSearchTabsCollection[currentTab];
        history.push(`${basePath}/${suggestion.value}`);
      }
    }
  };

  useEffect(() => {
    if (closeSearchTimeoutId) window.clearTimeout(closeSearchTimeoutId);
  }, [inputValue, isListVisible, currentTab, activeSuggestionIndex]);

  const handleOnTextInputFocus = () => {
    setSearchInputWidth(SIDE_NAV_WIDTH);
    setFocused(true);

    if (inputValue) {
      setIsListVisible(true);
      onSearchPanelVisibilityChange(true);
    }
  };

  // On out of focus, shrink the text input size to small
  function handleOnTextInputOnBlur() {
    setFocused(false);
    calculateTextInputWidth();
  }

  const activeTabSuggestions: GlobalSearchSuggestion[] =
    dataObjectsByTab[currentTab].suggestions;

  const updateScrollPosition = (newIndex: number) => {
    const elementId = getListItemId(activeTabSuggestions[newIndex].value);
    document
      .getElementById(elementId)
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  const onKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    switch (e.key) {
      case 'Enter':
      case 'Tab': {
        if (isListVisible) {
          handleSelect(activeTabSuggestions[activeSuggestionIndex]);
        }
        break;
      }
      case 'ArrowUp':
      case 'Up': {
        if (activeTabSuggestions.length > 0) {
          const newIndex =
            activeSuggestionIndex === 0
              ? activeTabSuggestions.length - 1
              : activeSuggestionIndex - 1;
          setActiveSuggestionIndex(newIndex);
          updateScrollPosition(newIndex);
        }
        break;
      }
      case 'ArrowDown':
      case 'Down': {
        if (activeTabSuggestions.length > 0) {
          const newIndex =
            (activeSuggestionIndex + 1) % activeTabSuggestions.length;
          setActiveSuggestionIndex(newIndex);
          updateScrollPosition(newIndex);
        }
        break;
      }
      case 'Escape': {
        setIsListVisible(false);
        onSearchPanelVisibilityChange(false);
        break;
      }
      default:
        break;
    }
  };

  const popupMaxHeight = Math.min(
    ABSOLUTE_MAX_LIST_HEIGHT,
    window.innerHeight -
      NAV_TOP_PADDING -
      NAV_LOGO_HEIGHT -
      NAV_SEARCHBAR_PADDING_TOP -
      TEXT_INPUT_HEIGHT -
      NAV_BOTTOM_PADDING,
  );

  // Search box props based on screen/width width
  const searchBoxProps = {
    placeholder: isSmall ? '' : 'Search here',
    startIcon: MagnifyingGlassIcon,
  };

  const tabKeys = Object.keys(GlobalSearchTabsCollection);

  return (
    <ChakraBox id={TOP_LEVEL_ID} width="100%">
      <ChakraBox
        backgroundColor="transparent"
        display={isListVisible ? 'block' : 'none'}
        height="100vh"
        left={0}
        onClick={() => {
          setIsListVisible(false);
          onSearchPanelVisibilityChange(false);
        }}
        onKeyDown={() => {
          setIsListVisible(false);
          onSearchPanelVisibilityChange(false);
        }}
        position="fixed"
        role="presentation"
        top={0}
        visibility={isListVisible ? 'visible' : 'hidden'}
        width="100vw"
        zIndex={isListVisible ? 1 : 0}
      />
      <ChakraBox
        backgroundColor={isSmall ? secondaryFive : neutralBackgroundPrimary}
        borderColor={isFocused ? 'transparent' : neutralTertiary}
        borderRadius="16px"
        borderStyle={isSmallVariant ? 'none' : 'solid'}
        borderWidth="1px"
        width={
          isSmall &&
          `calc(${searchInputWidth} - ${NAV_HORIZONTAL_PADDING * 2}px)`
        }
      >
        <NavTooltip
          id="GlobalSearch"
          isHidden={isFocused}
          marginLeft="-18px"
          renderTooltip={isSmall}
          tooltipText="Search"
        >
          <ChakraBox id="position-container" position="relative" zIndex={2}>
            <TextInput
              {...(isSmall && { variant: 'outline' })}
              {...searchBoxProps}
              autoComplete="off"
              autoFocus={isListVisible}
              backgroundColor={isFocused ? 'white' : 'none'}
              disableBackground
              disableBorder
              disableSpellCheck
              height={`${TEXT_INPUT_HEIGHT}px`}
              hoverBackgroundColor="none"
              id={TOP_LEVEL_ID}
              margin="0"
              onBlur={handleOnTextInputOnBlur}
              onChange={onChangeDebounced}
              onFocus={handleOnTextInputFocus}
              onKeyDown={onKeyDown}
              value={inputValue}
            />
            {listIsOpen && (
              <ChakraBox
                background="white"
                border={`1px solid ${neutralLine}`}
                borderRadius="16px"
                boxShadow={`0px 4px 16px ${neutralBackgroundPrimary}`}
                id={`${TOP_LEVEL_ID}__list-popup-container`}
                maxHeight={`${popupMaxHeight}px`}
                minWidth="550px"
                padding={`${POPUP_PADDING}px`}
                position="absolute"
                top={0}
                transform={`translate(0, ${TEXT_INPUT_HEIGHT + 4}px)`}
                zIndex={3}
              >
                <ChakraTabs
                  index={tabKeys.indexOf(currentTab)}
                  isLazy
                  onChange={(index) => {
                    setCurrentTab(tabKeys[index] as GlobalSearchTabs);
                  }}
                  variant="soft-rounded"
                >
                  <TabList
                    id={TOP_LEVEL_ID}
                    resultCounts={{
                      [GlobalSearchTabs.PATIENTS]: patients.totalCount,
                      [GlobalSearchTabs.PATIENTS_BY_CONTACT]:
                        contacts.totalCount,
                      [GlobalSearchTabs.INQUIRIES]: inquiries.totalCount,
                    }}
                  />
                  <ChakraTabPanels
                    height="100%"
                    id={`${TOP_LEVEL_ID}__tab-panels`}
                  >
                    {tabKeys.map((tab) => {
                      const {
                        handleSearch,
                        isLoading,
                        suggestions,
                        totalCount,
                      } = dataObjectsByTab[tab];
                      return (
                        <ChakraTabPanel
                          id={`${TOP_LEVEL_ID}__${tab}-tab-panel`}
                          key={tab}
                          padding={`${TAB_PANEL_PADDING}px 0px`}
                        >
                          <SearchResultsMaud
                            activeSuggestionIndex={activeSuggestionIndex}
                            fetchSuggestions={handleSearch}
                            getListItemId={getListItemId}
                            handleSelect={handleSelect}
                            id={`${TOP_LEVEL_ID}__${tab}-suggestions`}
                            inputValue={inputValue}
                            isLoading={isLoading || isDebouncedOptionsLoading}
                            maxHeight={
                              popupMaxHeight -
                              POPUP_PADDING * 2 -
                              TAB_LIST_HEIGHT -
                              TAB_PANEL_PADDING * 2
                            }
                            suggestions={suggestions}
                            totalOptionsCount={totalCount}
                          />
                        </ChakraTabPanel>
                      );
                    })}
                  </ChakraTabPanels>
                </ChakraTabs>
              </ChakraBox>
            )}
          </ChakraBox>
        </NavTooltip>
      </ChakraBox>
    </ChakraBox>
  );
};

export default GlobalSearchMaud;
